/**
 * Replying without a person.
 *
 * The useful part of this is small — say something outside office hours, greet a first-time
 * customer, answer the question you have already written an answer to. The dangerous part is
 * large, and it is all one thing: a machine that sends messages on its own can send a great
 * many of them to someone who did not want any.
 *
 * Two businesses both running an auto-responder will talk to each other until somebody
 * notices. A rule that fires per message rather than per conversation will answer a customer
 * five times while they are still typing. An auto-reply landing on a group tells forty people
 * the office is shut. Every guard below exists because of one of those, and the tests are
 * written against the failure rather than the feature.
 */

export type AutoReplyKind = 'greeting' | 'away' | 'keyword' | 'knowledge' | 'ai'

/** Per-conversation override of the workspace's AI-reply setting. `null` means no override —
 *  the conversation follows the workspace default (active once `aiReplies` is on) — so turning
 *  the feature on needs no per-conversation setup to start working. `'snoozed'` is set by the
 *  system the moment a person replies; `'thinking'` is set for the duration of a generation
 *  call, so it is a real, visible state rather than decorative. */
export type AiResponderState = 'active' | 'thinking' | 'snoozed' | 'inactive' | null

export interface HoursWindow {
  /** Minutes from midnight, in the workspace's own timezone. */
  openMinute: number
  closeMinute: number
}

export interface BusinessHours {
  /** IANA zone, e.g. Asia/Kolkata. Everything is evaluated in it, never in UTC. */
  timezone: string
  /** Indexed by day of week, Sunday first. An empty list means closed all day. */
  week: HoursWindow[][]
  /** ISO dates the business is shut regardless of the weekly pattern. */
  holidays: string[]
}

export interface KeywordReply {
  keywords: string[]
  message: string
  enabled?: boolean
}

export interface AutoReplyConfig {
  enabled: boolean
  hours: BusinessHours
  /** Sent once, the first time a number ever messages. */
  greeting: string | null
  /** Sent when a message arrives outside business hours. */
  awayMessage: string | null
  keywordReplies: KeywordReply[]
  /**
   * Answer from the knowledge base when the match is good enough. Off by default: sending a
   * customer an answer nobody checked is a different risk to sending them office hours.
   */
  answerFromKnowledge: boolean
  /** Minimum confidence before an answer is sent unattended. */
  knowledgeConfidence: 'likely' | 'strong'
  /**
   * Draft a reply with the workspace's configured AI model when nothing else above applies —
   * the last resort, tried only after a greeting, keyword match, knowledge answer, and the
   * away-message have all missed. Off by default: unlike the rest of this file, the exact
   * words are not known ahead of time, so an operator must opt in deliberately.
   */
  aiReplies: boolean
  /** How long before the same conversation may be auto-replied to again. */
  cooldownMinutes: number
  /** Auto-replying in a group tells everyone in it; off unless asked for. */
  replyInGroups: boolean
  /** If an agent has replied recently, the conversation is being handled by a person. */
  humanWindowMinutes: number
}

export const DEFAULT_AUTO_REPLY: AutoReplyConfig = {
  enabled: false,
  hours: {
    timezone: 'UTC',
    week: [[], [{ openMinute: 540, closeMinute: 1080 }], [{ openMinute: 540, closeMinute: 1080 }], [{ openMinute: 540, closeMinute: 1080 }], [{ openMinute: 540, closeMinute: 1080 }], [{ openMinute: 540, closeMinute: 1080 }], []],
    holidays: [],
  },
  greeting: null,
  awayMessage: 'Thanks for your message. Our office is closed right now — we will reply when we open.',
  keywordReplies: [],
  answerFromKnowledge: false,
  knowledgeConfidence: 'strong',
  aiReplies: false,
  cooldownMinutes: 120,
  replyInGroups: false,
  humanWindowMinutes: 30,
}

/* ---------------------------------------------------------------- business hours */

/**
 * The local wall-clock time in a given zone.
 *
 * Uses Intl rather than arithmetic on UTC offsets, because an offset is not a constant: a
 * business in London is open nine to five in both January and July, and anything that stores
 * a fixed offset tells half the year's customers the wrong thing.
 */
export function localTime(at: Date, timezone: string): { day: number; minute: number; date: string } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
  }).formatToParts(at)

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // 24:00 is how Intl reports midnight in some locales; it is the start of the day, not the end.
  const hour = Number(get('hour')) % 24

  return {
    day: Math.max(0, days.indexOf(get('weekday'))),
    minute: hour * 60 + Number(get('minute')),
    date: `${get('year')}-${get('month')}-${get('day')}`,
  }
}

/**
 * Whether the business is open.
 *
 * A window whose close is at or before its open runs past midnight — a kitchen open from six
 * in the evening until two in the morning is one window, not two, and treating it as an empty
 * range would tell every late customer the shop is shut while it is busiest.
 */
export function isOpen(hours: BusinessHours, at: Date): boolean {
  const { day, minute, date } = localTime(at, hours.timezone)
  if (hours.holidays.includes(date)) return false

  const today = hours.week[day] ?? []
  for (const window of today) {
    if (window.closeMinute > window.openMinute) {
      if (minute >= window.openMinute && minute < window.closeMinute) return true
    } else if (minute >= window.openMinute) {
      // Started today and still running.
      return true
    }
  }

  // A window opened yesterday and running past midnight covers this morning.
  const yesterday = hours.week[(day + 6) % 7] ?? []
  for (const window of yesterday) {
    if (window.closeMinute <= window.openMinute && minute < window.closeMinute) return true
  }

  return false
}

/* ---------------------------------------------------------------- deciding */

export interface AutoReplyContext {
  body: string | null
  kind: 'direct' | 'group'
  at: Date
  /** Whether this number has ever messaged before. */
  isFirstEverMessage: boolean
  /** When this conversation was last auto-replied to, if it has been. */
  lastAutoReplyAt: Date | null
  /** When a person on the team last replied here. */
  lastHumanReplyAt: Date | null
  /**
   * Recent inbound bodies, newest first. Used to spot a machine on the other end: a human
   * does not send the same sentence three times in as many minutes.
   */
  recentInbound: string[]
  /** Whether the contact has asked not to be messaged. */
  optedOut: boolean
  /** Best knowledge-base match, where one was found. */
  knowledge: { answer: string; confidence: 'none' | 'weak' | 'likely' | 'strong' } | null
  /** This conversation's per-chat override of the workspace's `aiReplies` setting. */
  aiResponderState: AiResponderState
}

export interface AutoReplyDecision {
  message: string | null
  kind: AutoReplyKind | null
  /** Why nothing was sent. Recorded, because "why did it not reply" is the common question. */
  reason: string | null
  /** Set only for `kind: 'ai'`. This function is pure and makes no network call, so it cannot
   *  hand back the actual generated text — it hands back permission instead, and the caller
   *  performs the generation (and the send) itself. Never set alongside a non-null `message`. */
  pending?: true
}

const NOTHING = (reason: string): AutoReplyDecision => ({ message: null, kind: null, reason })

/** Phrases that mean stop, in the words people actually use. */
const OPT_OUT = ['stop', 'unsubscribe', 'opt out', 'opt-out', 'do not message', "don't message"]

export function looksLikeOptOut(body: string | null): boolean {
  const text = (body ?? '').trim().toLowerCase()
  if (!text) return false
  // Matched as a whole message rather than a substring: "please stop sending my order late"
  // is a complaint, not an opt-out, and treating it as one loses the customer entirely.
  return OPT_OUT.includes(text.replace(/[.!]+$/, ''))
}

/**
 * Decides whether to send anything, and what.
 *
 * The order is deliberate. Every reason not to reply is checked before any reason to, so a
 * new reply type added later cannot accidentally sit above the loop guard.
 */
export function decideAutoReply(
  config: AutoReplyConfig,
  context: AutoReplyContext,
): AutoReplyDecision {
  if (!config.enabled) return NOTHING('Automatic replies are switched off.')

  // Telling a group of forty people the office is shut is spam to thirty-nine of them.
  if (context.kind === 'group' && !config.replyInGroups) {
    return NOTHING('Automatic replies are not sent to group chats.')
  }

  if (context.optedOut) return NOTHING('This contact asked not to be messaged.')
  if (looksLikeOptOut(context.body)) return NOTHING('This message is an opt-out.')

  /*
   * The loop guard.
   *
   * Two auto-responders will talk to each other indefinitely, and each one looks entirely
   * reasonable from its own side. A person does not send the same sentence three times in a
   * row; a machine does nothing else.
   */
  const body = (context.body ?? '').trim()
  if (body) {
    const identical = context.recentInbound.filter((entry) => entry.trim() === body).length
    if (identical >= 2) return NOTHING('The same message keeps arriving — this looks automated.')
  }

  // Someone is already dealing with this. An automated message on top of a real conversation
  // reads as though nobody is listening.
  if (context.lastHumanReplyAt) {
    const since = (context.at.getTime() - context.lastHumanReplyAt.getTime()) / 60_000
    if (since < config.humanWindowMinutes) {
      return NOTHING('Someone on the team is already replying here.')
    }
  }

  // One reply per conversation per window, however many messages arrive in it. A customer
  // sending four lines as they think is one arrival, not four.
  if (context.lastAutoReplyAt) {
    const since = (context.at.getTime() - context.lastAutoReplyAt.getTime()) / 60_000
    if (since < config.cooldownMinutes) {
      return NOTHING('This conversation was already answered automatically.')
    }
  }

  // --- from here, something may be sent ---

  if (context.isFirstEverMessage && config.greeting) {
    return { message: config.greeting, kind: 'greeting', reason: null }
  }

  const lower = body.toLowerCase()
  for (const rule of config.keywordReplies) {
    if (rule.enabled === false || !rule.message.trim()) continue
    if (rule.keywords.some((keyword) => keyword.trim() && lower.includes(keyword.toLowerCase()))) {
      return { message: rule.message, kind: 'keyword', reason: null }
    }
  }

  if (config.answerFromKnowledge && context.knowledge) {
    const enough =
      config.knowledgeConfidence === 'likely'
        ? ['likely', 'strong']
        : ['strong']
    if (enough.includes(context.knowledge.confidence)) {
      return { message: context.knowledge.answer, kind: 'knowledge', reason: null }
    }
  }

  if (!isOpen(config.hours, context.at) && config.awayMessage) {
    return { message: config.awayMessage, kind: 'away', reason: null }
  }

  // Last resort: an explicit per-chat 'inactive'/'snoozed' overrides the workspace default:
  // `null` (never touched) follows it, so turning the workspace setting on needs no
  // per-conversation setup to start working anywhere.
  const aiActive = context.aiResponderState === 'active' || context.aiResponderState === null
  if (config.aiReplies && aiActive) {
    return { message: null, kind: 'ai', reason: null, pending: true }
  }

  return NOTHING('Nothing applies to this message.')
}

/* ---------------------------------------------------------------- editing */

export function validateAutoReply(config: AutoReplyConfig): string[] {
  const problems: string[] = []

  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: config.hours.timezone })
  } catch {
    problems.push(`“${config.hours.timezone}” is not a timezone this understands.`)
  }

  if (config.hours.week.length !== 7) problems.push('Opening hours need an entry for each day.')

  for (const [day, windows] of config.hours.week.entries()) {
    for (const window of windows) {
      if (window.openMinute < 0 || window.openMinute > 1439 || window.closeMinute < 0 || window.closeMinute > 1440) {
        problems.push(`${DAY_NAMES[day]} has a time outside the day.`)
      }
    }
  }

  // Without a cooldown, a customer typing four lines gets four identical replies.
  if (config.cooldownMinutes < 1) problems.push('The gap between automatic replies must be at least a minute.')

  if (config.enabled && !config.greeting && !config.awayMessage && config.keywordReplies.length === 0 && !config.answerFromKnowledge && !config.aiReplies) {
    problems.push('Nothing is set up to send, so turning this on would do nothing.')
  }

  for (const rule of config.keywordReplies) {
    if (rule.keywords.filter((k) => k.trim()).length === 0) {
      problems.push('A keyword reply has no keywords.')
    } else if (!rule.message.trim()) {
      problems.push(`The reply for “${rule.keywords[0]}” is empty.`)
    }
  }

  return problems
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** Minutes from midnight as a 24-hour clock time. */
export function formatMinute(minute: number): string {
  const hours = Math.floor(minute / 60) % 24
  return `${String(hours).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
}

export function parseMinute(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 24 || minutes > 59) return null
  return hours * 60 + minutes
}

/** A one-line summary of the week, for the settings screen. */
export function describeHours(hours: BusinessHours): string {
  const open = hours.week
    .map((windows, day) => ({ day, windows }))
    .filter((entry) => entry.windows.length > 0)

  if (open.length === 0) return 'Closed all week'
  if (open.length === 7 && open.every((entry) => entry.windows.length === 1 && entry.windows[0].openMinute === 0 && entry.windows[0].closeMinute >= 1439)) {
    return 'Open all the time'
  }

  return open
    .map(
      (entry) =>
        `${DAY_NAMES[entry.day].slice(0, 3)} ${entry.windows
          .map((w) => `${formatMinute(w.openMinute)}–${formatMinute(w.closeMinute)}`)
          .join(', ')}`,
    )
    .join(' · ')
}
